import { z } from 'zod';
import { getPhotoSchema, photoOptionalSchema } from '../../../constants/sharedSchemas';

const DIPHTHERIA_DOSES_REQUIRED = 5;
const POLIO_DOSES_REQUIRED = 5;
const HEPATITIS_A_DOSES_REQUIRED = 2;
const HEPATITIS_B_DOSES_REQUIRED = 3;
const MEASLES_DOSES_REQUIRED = 2;
const MUMPS_DOSES_REQUIRED = 2;
const RUBELLA_DOSES_REQUIRED = 2;
const MENINGITIS_DOSES_REQUIRED = 1;

export enum MultipleVaccineDosesFormNames {
  diphtheria = 'diphtheriaTetanusPertussisDoses',
  polio = 'polioDoses',
  hepatitisA = 'hepatitisADoses',
  hepatitisB = 'hepatitisBDoses',
  measles = 'measlesDoses',
  mumps = 'mumpsDoses',
  rubella = 'rubellaDoses',
  meningitis = 'meningitisDoses',
}

export enum SingleVaccineDosesFormNames {
  COVID19 = 'covid19BoosterDose',
  tuberculosisTest = 'tuberculosisTest',
}

const vaccinationSchema = z.array(
  z.object({
    date: z.date({ required_error: 'Vaccination date is required' }),
    recordDocument: z
      .object({
        value: z.string().min(1, 'Please specify'),
        document: photoOptionalSchema,
      })
      .refine(
        (data) => {
          // Make document required if the value is "other"
          if (data.value === 'other') {
            return !!data.document.id;
          }
          return true;
        },
        {
          message: 'Vaccination proof is required',
          path: ['document'],
        },
      ),
  }),
);

const vaccinationSchemaOptional = z.array(
  z.object({
    date: z.date().optional(),
    recordDocument: z.object({ value: z.string().optional(), document: photoOptionalSchema }),
  }),
);

export const vaccinationsSchema = {
  required: z.object({
    vaccinationRecordFormDocument: getPhotoSchema('Vaccination Record Form'),
    [MultipleVaccineDosesFormNames.diphtheria]: vaccinationSchema,
    [MultipleVaccineDosesFormNames.polio]: vaccinationSchema,
    [MultipleVaccineDosesFormNames.hepatitisA]: vaccinationSchema,
    [MultipleVaccineDosesFormNames.hepatitisB]: vaccinationSchema,
    [MultipleVaccineDosesFormNames.measles]: vaccinationSchema,
    [MultipleVaccineDosesFormNames.mumps]: vaccinationSchema,
    [MultipleVaccineDosesFormNames.rubella]: vaccinationSchema,
    [MultipleVaccineDosesFormNames.meningitis]: vaccinationSchema,
    [SingleVaccineDosesFormNames.COVID19]: z.object({
      date: z.date({ required_error: 'Vaccination date is required' }),
      vaccineType: z.string().min(1, 'Vaccine type is required'),
      recordDocument: z
        .object({
          value: z.string().min(1, 'Please specify'),
          document: photoOptionalSchema,
        })
        .refine(
          (data) => {
            // Make document required if the value is "other"
            if (data.value === 'other') {
              return !!data.document.id;
            }
            return true;
          },
          {
            message: 'Vaccination proof is required',
            path: ['document'],
          },
        ),
    }),
    [SingleVaccineDosesFormNames.tuberculosisTest]: z.object({
      date: z.date({ required_error: 'Vaccination date is required' }),
      testStatus: z
        .object({
          result: z.string().min(1, 'Test status is required'),
          document: photoOptionalSchema,
        })
        .refine(
          (data) => {
            // Make xray document required if test result is positive
            if (data.result === 'positive') {
              return !!data.document.id;
            }
            return true;
          },
          {
            message: 'Negative chest x-ray is required',
            path: ['document'],
          },
        ),
    }),
  }),
  optional: z.object({
    vaccinationRecordFormDocument: photoOptionalSchema,
    [MultipleVaccineDosesFormNames.diphtheria]: vaccinationSchemaOptional,
    [MultipleVaccineDosesFormNames.polio]: vaccinationSchemaOptional,
    [MultipleVaccineDosesFormNames.hepatitisA]: vaccinationSchemaOptional,
    [MultipleVaccineDosesFormNames.hepatitisB]: vaccinationSchemaOptional,
    [MultipleVaccineDosesFormNames.measles]: vaccinationSchemaOptional,
    [MultipleVaccineDosesFormNames.mumps]: vaccinationSchemaOptional,
    [MultipleVaccineDosesFormNames.rubella]: vaccinationSchemaOptional,
    [MultipleVaccineDosesFormNames.meningitis]: vaccinationSchemaOptional,
    [SingleVaccineDosesFormNames.COVID19]: z.object({
      date: z.date().optional(),
      vaccineType: z.string().optional(),
      recordDocument: z.object({
        value: z.string().optional(),
        document: photoOptionalSchema,
      }),
    }),
    [SingleVaccineDosesFormNames.tuberculosisTest]: z.object({
      date: z.date().optional(),
      testStatus: z.object({
        result: z.string().optional(),
        document: photoOptionalSchema,
      }),
    }),
  }),
};

export type VaccinationsFormValues = z.infer<(typeof vaccinationsSchema)['required']> | z.infer<(typeof vaccinationsSchema)['optional']>;

export type VaccinationDose = VaccinationsFormValues[MultipleVaccineDosesFormNames.diphtheria][number];

const vaccinationDefaultValues = {
  date: undefined,
  recordDocument: { value: '', document: { id: '' } },
};

export const defaultValues: VaccinationsFormValues = {
  vaccinationRecordFormDocument: { id: '' },
  [MultipleVaccineDosesFormNames.diphtheria]: Array.from({ length: DIPHTHERIA_DOSES_REQUIRED }, () => vaccinationDefaultValues),
  [MultipleVaccineDosesFormNames.polio]: Array.from({ length: POLIO_DOSES_REQUIRED }, () => vaccinationDefaultValues),
  [MultipleVaccineDosesFormNames.hepatitisA]: Array.from({ length: HEPATITIS_A_DOSES_REQUIRED }, () => vaccinationDefaultValues),
  [MultipleVaccineDosesFormNames.hepatitisB]: Array.from({ length: HEPATITIS_B_DOSES_REQUIRED }, () => vaccinationDefaultValues),
  [MultipleVaccineDosesFormNames.measles]: Array.from({ length: MEASLES_DOSES_REQUIRED }, () => vaccinationDefaultValues),
  [MultipleVaccineDosesFormNames.mumps]: Array.from({ length: MUMPS_DOSES_REQUIRED }, () => vaccinationDefaultValues),
  [MultipleVaccineDosesFormNames.rubella]: Array.from({ length: RUBELLA_DOSES_REQUIRED }, () => vaccinationDefaultValues),
  [MultipleVaccineDosesFormNames.meningitis]: Array.from({ length: MENINGITIS_DOSES_REQUIRED }, () => vaccinationDefaultValues),

  [SingleVaccineDosesFormNames.COVID19]: {
    date: undefined,
    vaccineType: '',
    recordDocument: { value: '', document: { id: '' } },
  },
  [SingleVaccineDosesFormNames.tuberculosisTest]: {
    date: undefined,
    testStatus: { result: '', document: { id: '' } },
  },
};
